import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import {
  useGetPermissionsQuery,
  useGetPermissionUsersQuery,
  useAddUserToPermissionMutation,
  useRemoveUserFromPermissionMutation,
  useLazySearchUsersQuery,
  useGetUsersWithAccessQuery,
  Permission,
  User,
} from "../../../entities/admin-access";
import { useTranslation } from "react-i18next";
import { TRANSLATION_KEYS } from "../../../shared/config/i18n";

export const PermissionsManagement = () => {
  const { t } = useTranslation();
  const [selectedPermission, setSelectedPermission] =
    useState<Permission | null>(null);
  const [searchFirstName, setSearchFirstName] = useState("");
  const [searchLastName, setSearchLastName] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [searchResults, setSearchResults] = useState<User[]>([]);

  const { data: permissionsData, isLoading: isLoadingPermissions } =
    useGetPermissionsQuery();
  const { data: permissionUsersData, isLoading: isLoadingUsers } =
    useGetPermissionUsersQuery(selectedPermission?.id || 0, {
      skip: !selectedPermission,
    });
  const { data: usersWithAccessData } = useGetUsersWithAccessQuery();

  const [searchUsers, { isLoading: isSearching }] = useLazySearchUsersQuery();
  const [addUserToPermission] = useAddUserToPermissionMutation();
  const [removeUserFromPermission] = useRemoveUserFromPermissionMutation();
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  const handleSelectPermission = (permission: Permission) => {
    setSelectedPermission(permission);
    setSearchResults([]);
    setSearchFirstName("");
    setSearchLastName("");
    setSearchPhone("");
  };

  const handleSearch = async () => {
    if (
      (!searchFirstName || searchFirstName.length < 3) &&
      (!searchLastName || searchLastName.length < 3) &&
      (!searchPhone || searchPhone.length < 3)
    )
      return;

    try {
      const params = {
        phoneNumber: searchPhone,
        firstName: searchFirstName,
        lastName: searchLastName,
      };

      const result = await searchUsers(params).unwrap();
      if (result.result) {
        setSearchResults(result.users);
      }
    } catch (error) {
      console.error("Error searching users:", error);
    }
  };

  const handleAddUser = async (userId: string) => {
    if (!selectedPermission || pendingUserId) return;
    setPendingUserId(userId);

    try {
      await addUserToPermission({
        id: selectedPermission.id,
        userId,
      }).unwrap();

      setSearchResults([]);
      setSearchFirstName("");
      setSearchLastName("");
      setSearchPhone("");
    } catch (error) {
      console.error("Error adding user to permission:", error);
    } finally {
      setPendingUserId(null);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!selectedPermission || pendingUserId) return;
    setPendingUserId(userId);

    try {
      await removeUserFromPermission({
        id: selectedPermission.id,
        userId,
      }).unwrap();
    } catch (error) {
      console.error("Error removing user from permission:", error);
    } finally {
      setPendingUserId(null);
    }
  };

  useEffect(() => {
    if (permissionsData?.permissions?.length && !selectedPermission) {
      setSelectedPermission(permissionsData.permissions[0]);
    }
  }, [permissionsData, selectedPermission]);

  return (
    <Box
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Typography variant="h4" sx={{ flexShrink: 0 }}>
        {t(TRANSLATION_KEYS.ADMIN.PERMISSIONS_MANAGEMENT)}
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Permissions List */}
        <Paper
          sx={{
            width: 300,
            minWidth: 260,
            p: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Typography gutterBottom fontWeight={600} fontSize={18} sx={{ flexShrink: 0 }}>
            {t(TRANSLATION_KEYS.ADMIN.PERMISSIONS)}
          </Typography>
          {isLoadingPermissions ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List sx={{ flex: 1, overflow: "auto", py: 0 }}>
              {permissionsData?.permissions?.map((permission: Permission) => (
                <ListItem
                  key={permission.id}
                  sx={{
                    fontSize: 10,
                    borderRadius: 1,
                    backgroundColor:
                      selectedPermission?.id === permission.id
                        ? "action.selected"
                        : "inherit",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={() => handleSelectPermission(permission)}
                >
                  <ListItemText
                    primary={permission.title}
                    slotProps={{ primary: { fontSize: 14 } }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>

        {/* Permission Users */}
        <Paper
          sx={{
            flex: 2,
            p: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {selectedPermission ? (
            <>
              <Typography gutterBottom sx={{ flexShrink: 0 }}>
                {t(TRANSLATION_KEYS.ADMIN.USERS_WITH_PERMISSION)}
                {selectedPermission.title}
              </Typography>

              {/* Search Users */}
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mt: 1,
                  flexShrink: 0,
                }}
              >
                <Typography variant="subtitle2">
                  {t(TRANSLATION_KEYS.ADMIN.SEARCH_USERS)}
                </Typography>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    label={t("Имя")}
                    value={searchFirstName}
                    onChange={(e) => setSearchFirstName(e.target.value)}
                    variant="outlined"
                    fullWidth
                  />
                  <TextField
                    label={t("Фамилия")}
                    value={searchLastName}
                    onChange={(e) => setSearchLastName(e.target.value)}
                    variant="outlined"
                    fullWidth
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    label={t("Телефон")}
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                    variant="outlined"
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    onClick={handleSearch}
                    disabled={
                      isSearching ||
                      (searchFirstName.length < 3 &&
                        searchLastName.length < 3 &&
                        searchPhone.length < 3)
                    }
                    sx={{ minWidth: 100 }}
                  >
                    {isSearching ? (
                      <CircularProgress size={24} />
                    ) : (
                      t(TRANSLATION_KEYS.ACTIONS.SEARCH)
                    )}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography gutterBottom>
                      {t(TRANSLATION_KEYS.ADMIN.SEARCH_RESULTS)}
                    </Typography>
                    <List>
                      {searchResults.map((user) => (
                        <ListItem key={user.id}>
                          <ListItemText
                            primary={`${user.firstName} ${user.lastName}`}
                            secondary={user.phoneNumber}
                          />
                          <Button
                            variant="outlined"
                            onClick={() => handleAddUser(user.id)}
                            disabled={pendingUserId !== null}
                          >
                            {pendingUserId === user.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              t(TRANSLATION_KEYS.ACTIONS.ADD)
                            )}
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                    <Divider sx={{ my: 2 }} />
                  </Box>
                )}

                {/* Current Permission Users */}
                {isLoadingUsers ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    <Typography gutterBottom>
                      {t(TRANSLATION_KEYS.ADMIN.CURRENT_USERS)}
                    </Typography>
                    {permissionUsersData?.users?.length ? (
                      <List>
                        {permissionUsersData.users.map((user: User) => (
                          <ListItem key={user.id}>
                            <ListItemText
                              primary={`${user.firstName} ${user.lastName}`}
                              secondary={user.phoneNumber}
                            />
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => handleRemoveUser(user.id)}
                              disabled={pendingUserId !== null}
                            >
                              {pendingUserId === user.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                t(TRANSLATION_KEYS.ACTIONS.REMOVE)
                              )}
                            </Button>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography color="text.secondary">
                        {t(TRANSLATION_KEYS.ADMIN.NO_USERS_WITH_PERMISSION)}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </>
          ) : (
            <Typography color="text.secondary">
              {t(TRANSLATION_KEYS.ADMIN.SELECT_PERMISSION)}
            </Typography>
          )}
        </Paper>

        {/* Users with Access */}
        <Paper
          sx={{
            flex: 1,
            p: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Typography gutterBottom sx={{ flexShrink: 0 }}>
            {t(TRANSLATION_KEYS.ADMIN.ALL_USERS_WITH_ACCESS)}
          </Typography>
          <List sx={{ flex: 1, overflow: "auto", py: 0 }}>
            {usersWithAccessData?.users?.map((user: User) => (
              <ListItem key={user.id} alignItems="flex-start">
                <ListItemText
                  primary={`${user.firstName} ${user.lastName}`}
                  secondary={
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {user.permissions.map((perm: Permission) => (
                        <Chip
                          key={perm.id}
                          label={perm.title}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>
    </Box>
  );
};
